import './DashboardV2Modal.less';

import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { plural } from '../../../../../shared/helpers';
import { ProjectCategory } from '../../../../../shared/projectsV2';
import { useAddToast } from '../../../../contexts/ToastContext';
import * as ProjectsV2Store from '../../../../redux/projectsV2';
import { BUTTON_SIZE, BUTTON_TYPE, ButtonGroup, ErrorAlert, InlineLoaderButton } from '../../../generic';
import Button from '../../../generic/Button';
import Flex from '../../../generic/Flex';
import LabeledTextInput from '../../../generic/Forms/LabeledTextInput';
import type { Project, ReleaseGroup } from '../../../../../shared/projectsV2';

import ProjectListComponent from './ProjectListComponent';

const CATEGORY_LABEL: Record<ProjectCategory, string> = {
  projects: 'project',
  releaseGroups: 'release group',
};

type DeleteModalProps = {
  category: ProjectCategory;
  show: boolean;
  selectedProjects: Project[] | ReleaseGroup[] | 'all';
  selectedProjectsCount: number;
  onHide: () => void;
  onDelete: () => Promise<void>;
};

const DeleteModal: VoidFunctionComponent<DeleteModalProps> = ({
  category,
  show,
  selectedProjects,
  selectedProjectsCount,
  onHide,
  onDelete,
}) => {
  const dispatch = useDispatch();
  const { loading: isLoading, errors } = useSelector(ProjectsV2Store.selectors.getAsyncStatusUpdateProjects);
  const [bulkDeleteConfirmationText, setBulkDeleteConfirmationText] = useState<string>('');

  const addToast = useAddToast();

  const onEnter = () => {
    setBulkDeleteConfirmationText('');
  };

  const isDeleteButtonDisabled = isLoading || bulkDeleteConfirmationText !== 'DELETE';

  const handleDelete = async () => {
    await onDelete();
    dispatch(ProjectsV2Store.actions.refetchProjects());
    addToast({ type: 'success', content: `Successfully deleted ${plural(selectedProjectsCount, CATEGORY_LABEL[category])}.` });
    onHide();
  };

  return (
    <Modal
      backdrop="static"
      animation
      show={show}
      onEnter={onEnter}
      onHide={onHide}
      onEscapeKeyDown={onHide}
      className="projects-dashboard-v2-modal"
    >
      <Modal.Header bsClass="projects-dashboard-v2-modal__header">
        <Modal.Title bsClass="projects-dashboard-v2-modal__header__title">
          {`Delete ${plural(selectedProjectsCount, CATEGORY_LABEL[category])}`}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body bsClass="projects-dashboard-v2-modal__body">
        <div className="projects-dashboard-v2-modal__body__message">
          <span className="projects-dashboard-v2-modal__body__message__strong">
            {`${selectedProjectsCount.toLocaleString()} ${plural(selectedProjectsCount, CATEGORY_LABEL[category])}`}
          </span>
          &nbsp;will be deleted.&nbsp;
          <span className="projects-dashboard-v2-modal__body__message__alert">
            This action cannot be undone.
          </span>
        </div>
        {selectedProjects !== 'all' && <ProjectListComponent items={selectedProjects} category={CATEGORY_LABEL[category]} />}
        <div className="projects-dashboard-v2-modal__body__message__list">
          <span>You will lose {CATEGORY_LABEL[category]} data, including:</span>
          <ul>
            <li>Issue resolutions</li>
            <li>Oauth tokens</li>
            <li>Notification settings</li>
            <li>Webhook settings</li>
          </ul>
        </div>
        <Flex justify="flex-start" align="flex-start">
          <LabeledTextInput
            labelVariant="legacy"
            label="Type DELETE to confirm"
            onChange={setBulkDeleteConfirmationText}
            isDisabled={isLoading}
            value={bulkDeleteConfirmationText}
            placeholder="Are you sure?"
          />
        </Flex>
        {errors &&
          <div className="projects-dashboard-v2-modal__body__errors">
            <ErrorAlert status={{ errors }} />
          </div>}
      </Modal.Body>
      <Modal.Footer bsClass="projects-dashboard-v2-modal__footer">
        <ButtonGroup>
          <Button
            type={BUTTON_TYPE.SECONDARY}
            size={BUTTON_SIZE.MEDIUM}
            isDisabled={isLoading}
            onClick={onHide}
          >
            Cancel
          </Button>
          <InlineLoaderButton
            type={BUTTON_TYPE.ERROR}
            size={BUTTON_SIZE.MEDIUM}
            isSubmitButton
            isLoading={isLoading}
            isDisabled={isDeleteButtonDisabled}
            onClick={handleDelete}
          >
            Delete
          </InlineLoaderButton>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

type DeleteProjectsModalProps = {
  show: boolean;
  selectedProjects: Project[] | 'all';
  selectedProjectsCount: number;
  onHide: () => void;
};

export const DeleteProjectsModal: VoidFunctionComponent<DeleteProjectsModalProps> = ({
  show,
  selectedProjects,
  selectedProjectsCount,
  onHide,
}) => {
  const dispatch = useDispatch();

  const onDelete = () => {
    return dispatch(ProjectsV2Store.actions.deleteSelectedProjects());
  };

  return (
    <DeleteModal
      category="projects"
      show={show}
      selectedProjects={selectedProjects}
      selectedProjectsCount={selectedProjectsCount}
      onHide={onHide}
      onDelete={onDelete}
    />
  );
};

type DeleteReleaseGroupsModalProps = {
  show: boolean;
  selectedReleaseGroups: ReleaseGroup[] | 'all';
  selectedReleaseGroupsCount: number;
  onHide: () => void;
};

export const DeleteReleaseGroupsModal: VoidFunctionComponent<DeleteReleaseGroupsModalProps> = ({
  show,
  selectedReleaseGroups,
  selectedReleaseGroupsCount,
  onHide,
}) => {
  const dispatch = useDispatch();

  const onDelete = () => {
    return dispatch(ProjectsV2Store.actions.deleteSelectedProjects());
  };

  return (
    <DeleteModal
      category={ProjectCategory.ReleaseGroups}
      show={show}
      selectedProjects={selectedReleaseGroups}
      selectedProjectsCount={selectedReleaseGroupsCount}
      onHide={onHide}
      onDelete={onDelete}
    />
  );
};
